import { COLORS } from '@btime/constants'
import { any, pathEq, propEq, trim } from 'ramda'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { DndHandler } from '../../../../components/dnd/DndHandler'
import { hasValue } from '../../../../utils/helpers/value'
import { useChecklistForm } from '../_form/ChecklistFormHandler'
import CategoriesList from './categories/CategoriesList'
import ConditionsModal from './questions/conditions/ConditionsModal'
import FlexGrid, { FlexCol } from '../../../../components/structure/FlexGrid'
import QuestionConfigForm from './questions/form/QuestionConfigForm'
import QuestionTypesList from './questionTypes/QuestionTypesList'
import QuestionTypesLoader from './questionTypes/_load/QuestionTypesLoader'
import Message from '../../../../components/messenger/Message'

const checkQuestionsNames = any(question => !trim(question.name) || checkQuestionsNames(question.children))
const checkQuestionsOptions = questions =>
  questions.some(
    question =>
      (['select', 'multiple'].includes(question.questionType?.name) &&
        (!hasValue(question.options) || question.options?.every(item => !item.name))) ||
      checkQuestionsOptions(question.children)
  )
const checkQuestionsConditions = (questions, parentQuestion) => {
  return questions.some(question => {
    if (checkQuestionConditions(question.displayConditions, parentQuestion?.options)) return true
    if (checkQuestionConditions(question.nonComplianceConditions, question?.options)) return true
    return checkQuestionsConditions(question.children || [], question)
  })
}
const checkQuestionConditions = (conditions = [], options = []) => {
  return conditions?.some(condition => {
    if (condition.operator !== 'within_refs') return false

    return condition.value?.some(conditionOption => {
      return !options.some(propEq('reference', conditionOption.reference))
    })
  })
}
const checkEmptyCalc = questions =>
  questions.some(question => ['calc'].includes(question.questionType?.name) && !hasValue(question.data?.calc))
const checkQuestionsEmptyBlocks = any(
  question =>
    (question.questionType?.name === 'block' && !hasValue(question.children)) ||
    checkQuestionsEmptyBlocks(question.children)
)

const checkEmptyIntegration = questions =>
  questions.some(
    question => ['integration'].includes(question.questionType?.name) && !hasValue(question.data?.integration)
  )

const checkQuestionsBlocksInBlocks = any(
  question =>
    (question.questionType?.name === 'block' && question.children.some(pathEq(['questionType', 'name'], 'block'))) ||
    checkQuestionsBlocksInBlocks(question.children)
)

export const useQuestionsStepDef = () => {
  const { t } = useTranslation('editChecklist')
  const form = useChecklistForm()

  return React.useMemo(
    () => ({
      title: 'questionsStep.title',
      Component: QuestionsStep,
      fullWidth: true,
      fullScreen: true,
      validate: () => {
        if (!form.categories.length) {
          return [false, t('validationAddAtLeastOneCategory')]
        }

        if (form.categories.some(cat => !cat.selectedCategory.id)) {
          return [false, t('validationGroupsWithoutCategory')]
        }

        if (form.categories.some(cat => !cat.children.length)) {
          return [false, t('validationCategoriesWithoutQuestions')]
        }

        if (form.categories.some(cat => checkQuestionsNames(cat.children))) {
          return [false, t('validationQuestionsWithoutLabel')]
        }

        if (form.categories.some(cat => checkQuestionsConditions(cat.children))) {
          return [false, t('validationInvalidConditions')]
        }

        if (form.categories.some(cat => checkQuestionsOptions(cat.children))) {
          return [false, t('validationQuestionsWithoutOptions')]
        }

        if (form.categories.some(cat => checkQuestionsEmptyBlocks(cat.children))) {
          return [false, t('validationEmptyBlocks')]
        }

        if (form.categories.some(cat => checkQuestionsBlocksInBlocks(cat.children))) {
          return [false, t('validationBlocksInBlocks')]
        }

        if (form.categories.some(cat => checkEmptyCalc(cat.children))) {
          return [false, t('validationEmptyCalc')]
        }

        if (form.categories.some(cat => checkEmptyIntegration(cat.children))) {
          return [false, t('validationEmptyIntegration')]
        }

        return [true]
      }
    }),
    [form.categories, t]
  )
}

const QuestionsStep = () => {
  const { t } = useTranslation('editChecklist')
  const { totalQuestions, byPassLimit, maxQuestions } = useChecklistForm()

  return (
    <DndHandler>
      {/*<PreviewMessage />*/}
      {!byPassLimit && (
        <Message
          title={t('questionsRemaining', { remaining: maxQuestions - totalQuestions, total: maxQuestions })}
          useMarkdown={true}
          color={COLORS.dark3}
          bgColor={COLORS.lightYellow}
        />
      )}

      <FlexGrid align="stretch" justify="flex-start">
        <FlexCol width="15%" bgColor={COLORS.white}>
          <QuestionTypesLoader>
            <QuestionTypesList />
          </QuestionTypesLoader>
        </FlexCol>

        <FlexCol flex={1}>
          <CategoriesList />
        </FlexCol>

        <FlexCol width="22%" bgColor={COLORS.white}>
          <QuestionConfigForm />
        </FlexCol>
      </FlexGrid>

      <ConditionsModal />
    </DndHandler>
  )
}

export default QuestionsStep
